import React, { useMemo, useState } from 'react';
import CardCommon from '@op/components/CardCommon';
import SortBySelect from '@op/components/FilterBoxCommon/filters/single-select-factory';
import { NO_DATE } from '@op/constants';
import useLanguage from '@op/hooks/useLanguage';
import { barIcon, commonBarHIcon, lineIcon, pieIcon, stackLineIcon, SymbolType, tableIcon, vsIcon } from '@op/constants/chart';
import { goToCompetitorKeyMetrics } from '@op/utils/url';
import { useRealtimeCommunityTimeline } from '../../hook';
import { IRealtimeCommunity } from '../../type';
import Table from './Table';
import { TABLE_HEADER_TYPE } from '../../constant';
import Chart from './Chart';
import PieChart from './PieChart';
import { downloadAction } from '@op/pages/download/utils';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';

export default function CommunityChart(props: IRealtimeCommunity) {
  const {
    filter,
    dropdown,
    title,
    valueKey,
    formatValue,
    isPercent,
    gameInfo,
    chartData,
    loading,
    byType,
    setByType,
  } = props;

  const { formatMessage } = useLanguage();
  const [chartType, setChartType] = useState<SymbolType>('stack_line');


  const filterParams = useMemo(() => ({
    ...filter,
    by_type: byType,
    metrics: [valueKey]
  }), [filter, byType, valueKey]);

  const { data } = useRealtimeCommunityTimeline({
    filter: filterParams,
    data: chartData,
    extraData: {
      metricsKey: [{
        title,
        value: valueKey
      }]
    }
  });

  function changeChartType(type: any) {
    if (type === 'vs') {
      goToCompetitorKeyMetrics(history, filterParams, 'Community');
    } else {
      setChartType(type || 'bar');
    }
  }

  const renderComponent = useMemo(() => {
    if (chartType === 'table') {
      return (
        <Table
          header={TABLE_HEADER_TYPE?.[byType] || ''}
          title={title}
          data={data?.tableRecord || []}
          filter={filterParams}
        />
      );
    }

    if (chartType === 'pie' || chartType === 'common-bar') {
      return (
        <PieChart
          chartType={chartType}
          data={data}
          formatValue={formatValue}
          title={title}
          isPercent={isPercent}
          filter={filterParams}
          byType={byType}
          gameInfo={gameInfo}
        />
      );
    }

    return (
      <Chart
        chartType={chartType}
        data={data}
        formatValue={formatValue}
        title={title}
        isPercent={isPercent}
        filter={filterParams}
        byType={byType}
        gameInfo={gameInfo}
        valueKey={valueKey}
      />
    );
  }, [chartType, data]);

  return (
    <CardCommon
      title={
        <SortBySelect
          defaultValue={byType}
          onChange={(key: string, value: any) => {
            setByType(value);
            buriedBtnPoint('RO_Community_ByType', value);
          }}
          defaultParams={{
            options: dropdown,
            btnTextPrefix: formatMessage({ id: title || NO_DATE.EMPTY }),
            matchButtonWidth: true,
          }}
        />
      }
      operations={[
        vsIcon,
        pieIcon,
        tableIcon,
        lineIcon,
        stackLineIcon,
        commonBarHIcon,
        barIcon,
      ]}
      operationInfo={[chartType, changeChartType]}
      loading={loading}
      loadingHeight={366}
      emptyHeight={366}
      className='dbo-realtime-community-chart'
      contentCard
      hasDownload={!data?.isEmpty}
      empty={data?.isEmpty ?? true}
      handleDownloadOnClick={() => {
        const byTypeLabel = dropdown?.find((val) => val.value === byType)?.text;
        downloadAction({
          key: 'RealtimeOverviewMetricsDownload',
          fileName: `Realtime Community ${title} - ${byTypeLabel}`,
          filter: {
            ...filter,
            ...gameInfo,
          },
          auxiliaryParams: {
            columns: [
              {
                name: TABLE_HEADER_TYPE?.[byType] || '',
                value: 'key'
              }
              , {
                name: title,
                value: 'value'
              }]
          },
          data: data?.tableRecord || []
        });
      }}
      logBtnId='RO_Community_Chart'
      extraInfo={{
        byType,
        metrics: [valueKey]
      }}
    >
      <>
        {renderComponent}
      </>
    </CardCommon >
  );
};